import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ETypeOfNotification,
  RColumn,
  RemoteTable,
  RemoteTableRef,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useDispatch } from 'react-redux'
import {
  StockItemTypeContractCreateResultDTO,
  StockItemTypeContractListItemDTO,
  StockItemTypeContractListQueryParams,
  StockItemTypeContractOptionDTO,
  StockItemTypeContractOptionsDTO,
} from '../../dto/stockItemTypeContract/StockItemTypeContractDTO'
import {
  deactivateStockItemTypeContract,
  getStockItemTypeContractHealthEntities,
  getStockItemTypeContractOptions,
  getStockItemTypeContracts,
} from '../../services/StockItemTypeContractController'
import { parseApiError } from '../../services/apiError'
import DeactivateStockItemTypeContractModal from './components/DeactivateStockItemTypeContractModal'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import StockItemTypeContractFormView from './StockItemTypeContractFormView'
import { formatIsoDate, formatPercentage } from './stockItemTypeContractFormat'
import './ContractsByItemType.scss'

const TABLE_ID = 'StockItemTypeContractsTable'
const EMPTY_OPTIONS: StockItemTypeContractOptionsDTO = { stockItemTypes: [], modalities: [] }

function hasFilters(params: StockItemTypeContractListQueryParams): boolean {
  const filterDTO = params.filterDTO
  if (typeof filterDTO !== 'object' || filterDTO === null) return false
  const filters = (filterDTO as { filters?: unknown }).filters
  return Array.isArray(filters) && filters.length > 0
}

/** Una sola línea (§3.8.2): cuántos contratos se crearon. */
function describeCreation(result: StockItemTypeContractCreateResultDTO | null): string {
  const created = result?.created ?? 1
  return created === 1 ? 'Contrato creado' : `${created} contratos creados`
}

/**
 * Contratos > Por Tipo de Ítem. Reemplaza a la ventana Vaadin "Agregar contrato por tipo de ítem"
 * (Facturación > Contratos): listado de contratos activos con alta, edición y desactivación.
 */
export default function ContractsByItemTypeView() {
  const dispatch = useDispatch()
  const tableRef = useRef<RemoteTableRef>(null)

  const [healthEntities, setHealthEntities] = useState<StockItemTypeContractOptionDTO[]>([])
  const [options, setOptions] = useState<StockItemTypeContractOptionsDTO>(EMPTY_OPTIONS)
  const [filtersApplied, setFiltersApplied] = useState(false)

  const [formVisible, setFormVisible] = useState(false)
  const [contractToEdit, setContractToEdit] = useState<StockItemTypeContractListItemDTO | null>(null)

  const [contractToDeactivate, setContractToDeactivate] = useState<StockItemTypeContractListItemDTO | null>(null)
  const [deactivating, setDeactivating] = useState(false)

  // §3.8.2: éxito 5 s, error 8 s; una sola línea, sin texto que repita el título.
  const notifySuccess = useCallback((title: string) => {
    dispatch(setNotification({
      title,
      typeOfNotification: ETypeOfNotification.INFO,
      icon: 'pi-check-circle',
      duration: 5000,
    }))
  }, [dispatch])

  const notifyError = useCallback((title: string) => {
    dispatch(setNotification({
      title,
      typeOfNotification: ETypeOfNotification.ERROR,
      icon: 'pi-times-circle',
      duration: 8000,
    }))
  }, [dispatch])

  useEffect(() => {
    let cancelled = false
    Promise.all([getStockItemTypeContractHealthEntities(), getStockItemTypeContractOptions()])
      .then(([loadedHealthEntities, loadedOptions]) => {
        if (cancelled) return
        setHealthEntities(loadedHealthEntities)
        setOptions(loadedOptions)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        parseApiError(error)
        notifyError('No se pudieron cargar las opciones del formulario. Intentá de nuevo.')
      })
    return () => {
      cancelled = true
    }
  }, [notifyError])

  const loadContracts = useCallback(
    (offSet: number, pageSize: number, params: StockItemTypeContractListQueryParams = {}) => {
      setFiltersApplied(hasFilters(params))
      return getStockItemTypeContracts(offSet, pageSize, params).catch((error: unknown) => {
        parseApiError(error)
        notifyError('No se pudieron cargar los contratos. Intentá de nuevo.')
        return [] as StockItemTypeContractListItemDTO[]
      })
    },
    [notifyError],
  )

  function openCreate() {
    setContractToEdit(null)
    setFormVisible(true)
  }

  const openEdit = useCallback((contract: StockItemTypeContractListItemDTO) => {
    setContractToEdit(contract)
    setFormVisible(true)
  }, [])

  function closeForm() {
    setFormVisible(false)
    setContractToEdit(null)
  }

  function handleSaved(isEdition: boolean, result: StockItemTypeContractCreateResultDTO | null) {
    closeForm()
    notifySuccess(isEdition ? 'Cambios guardados' : describeCreation(result))
    tableRef.current?.refreshTable?.()
  }

  function handleStale(message: string) {
    closeForm()
    notifyError(message)
    tableRef.current?.refreshTable?.()
  }

  async function confirmDeactivate() {
    if (!contractToDeactivate || deactivating) return
    setDeactivating(true)
    try {
      await deactivateStockItemTypeContract(contractToDeactivate.stockitemtypecontract)
      notifySuccess('Contrato desactivado')
    } catch (error: unknown) {
      const apiError = parseApiError(error)
      const isKnown = apiError.errorCode === 'STOCK_ITEM_TYPE_CONTRACT_INACTIVE'
        || apiError.errorCode === 'STOCK_ITEM_TYPE_CONTRACT_NOT_FOUND'
      notifyError(isKnown ? apiError.message : 'No se pudo desactivar el contrato. Intentá de nuevo.')
    } finally {
      setDeactivating(false)
      setContractToDeactivate(null)
      tableRef.current?.refreshTable?.()
    }
  }

  // Acciones por fila: menú de tres puntos, como el resto de las tablas del sistema. Un ícono fijo por acción (§3.1).
  const rowActions = useCallback((contract: StockItemTypeContractListItemDTO) => {
    const menuItems: LocationActionItem[] = [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: () => openEdit(contract),
      },
      {
        label: 'Desactivar',
        icon: 'pi pi-ban',
        action: () => setContractToDeactivate(contract),
      },
    ]

    return (
      <LocationActionMenu
        items={menuItems}
        ariaLabel='Opciones del contrato'
        buttonId={`stcMenu_${contract.stockitemtypecontract}`}
        buttonClassName='p-button-text nv-icon'
        panelClassName='stc-action-menu-panel'
      />
    )
  }, [openEdit])

  // Alta y edición van en una pantalla aparte, en lugar de la tabla (mismo patrón que Kits y Feriados).
  if (formVisible) {
    return (
      <StockItemTypeContractFormView
        key={contractToEdit?.stockitemtypecontract ?? 'new'}
        contract={contractToEdit}
        healthEntities={healthEntities}
        options={options}
        onBack={closeForm}
        onSaved={handleSaved}
        onError={notifyError}
        onStale={handleStale}
      />
    )
  }

  return (
    <div className='nv-scope nv-page stc-view'>
      <div className='stc-header'>
        <h1 className='nv-h1'>Contratos por tipo de ítem</h1>
        <Button
          id='buttonCreateStockItemTypeContract'
          type='button'
          className='buttonMain'
          label='Crear'
          icon='pi pi-plus-circle'
          onClick={openCreate}
        />
      </div>

      <RemoteTable
        ref={tableRef}
        idTable={TABLE_ID}
        dataKey='stockitemtypecontract'
        className='stc-table'
        getItems={loadContracts}
        filterRow
        resizableColumns
        tableMinWidth='60rem'
        notFoundText={filtersApplied ? 'No hay resultados para estos filtros.' : 'Todavía no hay contratos cargados.'}
        rowActions={rowActions}
      >
        <RColumn
          field='healthentityname'
          header='Obra social'
          grow={1.5}
          sortable
          filter
          filterElement={stringFilterTemplate}
        />
        <RColumn
          field='healthentityplanname'
          header='Plan'
          sortable
          filter
          filterElement={stringFilterTemplate}
        />
        <RColumn
          field='stockitemtypedesc'
          header='Tipo de ítem'
          sortable
          filter
          filterElement={stringFilterTemplate}
        />
        <RColumn
          field='contractattentionmodalitydesc'
          header='Modalidad'
          sortable
          filter
          filterElement={stringFilterTemplate}
        />
        <RColumn
          field='percentage'
          header='Cobertura'
          grow={0.6}
          sortable
          className='text-right'
          body={(contract: StockItemTypeContractListItemDTO) => formatPercentage(contract.percentage)}
        />
        <RColumn
          field='validfrom'
          header='Válido desde'
          grow={0.75}
          sortable
          className='text-center'
          body={(contract: StockItemTypeContractListItemDTO) => formatIsoDate(contract.validfrom)}
        />
        <RColumn
          field='validuntil'
          header='Válido hasta'
          grow={0.9}
          sortable
          className='text-center'
          body={(contract: StockItemTypeContractListItemDTO) => (
            contract.validuntil ? formatIsoDate(contract.validuntil) : 'Sin fecha de fin'
          )}
        />
      </RemoteTable>

      <DeactivateStockItemTypeContractModal
        visible={contractToDeactivate != null}
        contract={contractToDeactivate}
        confirming={deactivating}
        onCancel={() => setContractToDeactivate(null)}
        onConfirm={() => { void confirmDeactivate() }}
      />
    </div>
  )
}
